/**
 * CashFlowChart Component
 * Visualizes 30/60/90 day cash flow forecast using Chart.js
 */

import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import type { CashFlowForecast } from '@/types/metrics';
import { formatCurrency } from '@/utils/formatters';
import './CashFlowChart.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface CashFlowChartProps {
  data: CashFlowForecast;
  loading?: boolean;
}

export function CashFlowChart({ data, loading = false }: CashFlowChartProps) {
  if (loading) {
    return (
      <div className="cash-flow-chart cash-flow-chart--loading" aria-busy="true">
        <div className="skeleton skeleton--chart"></div>
      </div>
    );
  }

  const chartData = {
    labels: ['Now', '30 Days', '60 Days', '90 Days'],
    datasets: [
      {
        label: 'Expected Cash Flow',
        data: [0, data.day30, data.day60, data.day90],
        borderColor: '#075156', // Marine
        backgroundColor: 'rgba(7, 81, 86, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: '#075156',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#17232d',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 12,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function (context) {
            return formatCurrency(Number(context.parsed.y));
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return formatCurrency(Number(value));
          },
          color: '#6b7280',
          font: {
            family: 'Montserrat, sans-serif',
            size: 12,
          },
        },
        grid: {
          color: 'rgba(229, 238, 239, 0.5)',
        },
      },
      x: {
        ticks: {
          color: '#374151',
          font: {
            family: 'Montserrat, sans-serif',
            size: 12,
            weight: 500,
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="cash-flow-chart">
      <Line data={chartData} options={options} aria-label="Cash flow forecast chart" />
    </div>
  );
}
